import { Box } from "rebass/styled-components";

import { useFeaturedCompetition } from "../CompetitionFeature";

import { INavCategory, INavItem, isPathActive, visibleItems } from "../links";
import {
  ItemAnchor,
  ItemBadge,
  ItemDescription,
  ItemGrid,
  ItemIcon,
  ItemLink,
  ItemTitle,
  ItemTitleRow,
  Panel,
  PanelBlurb,
  PanelCta,
  PanelCtaAnchor,
  PanelEyebrow,
  PanelHeadline,
  PanelInner,
  PanelPitch,
} from "../styledComponents";

// Exported for the mobile sheet, which renders the same row inside an
// accordion rather than repeating the icon/badge/active markup.
export const NavItem = ({
  item,
  pathname,
  onNavigate,
}: {
  item: INavItem;
  pathname: string;
  onNavigate: () => void;
}) => {
  const active = isPathActive(item.to, pathname);
  const Icon = item.icon;
  const body = (
    <>
      <ItemTitleRow>
        <ItemIcon aria-hidden="true">
          <Icon />
        </ItemIcon>
        <ItemTitle>{item.title}</ItemTitle>
        {item.badge && <ItemBadge>{item.badge}</ItemBadge>}
      </ItemTitleRow>
      <ItemDescription>{item.description}</ItemDescription>
    </>
  );

  if (item.href)
    return (
      <ItemAnchor
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
      >
        {body}
      </ItemAnchor>
    );

  return (
    <ItemLink
      to={item.to || "/"}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
    >
      {body}
    </ItemLink>
  );
};

export const MegaMenu = ({
  category,
  isLoggedIn,
  panelId,
  pathname,
  standalone,
  onNavigate,
  onMouseEnter,
  onMouseLeave,
}: {
  category: INavCategory;
  isLoggedIn: boolean;
  panelId: string;
  pathname: string;
  standalone?: boolean;
  onNavigate: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) => {
  const { cta } = category;
  // A live competition takes over the panel's art and its CTA, so Compete
  // points at the thing running right now rather than a static list.
  const competition = useFeaturedCompetition(
    category.featured === "competition",
  );
  const ctaLabel = competition
    ? `Enter ${competition.title}`
    : isLoggedIn
    ? cta.labelLoggedIn
    : cta.label;
  const ctaTo = competition
    ? `/competitions/${competition._id}`
    : (isLoggedIn ? cta.toLoggedIn : cta.to) || "/";

  return (
    <Panel
      id={panelId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={category.label}
    >
      <PanelInner $standalone={standalone}>
        {/* Left third: the panel sells while it navigates. */}
        <PanelPitch $art={category.art} $glass={category.glass}>
          <PanelEyebrow>{category.eyebrow}</PanelEyebrow>
          <PanelHeadline>{category.label}</PanelHeadline>
          <PanelBlurb>{category.blurb}</PanelBlurb>
          {cta.href ? (
            <PanelCtaAnchor
              $accent={category.accent}
              href={cta.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavigate}
            >
              {ctaLabel}
            </PanelCtaAnchor>
          ) : (
            <PanelCta to={ctaTo} $accent={category.accent} onClick={onNavigate}>
              {ctaLabel}
            </PanelCta>
          )}
        </PanelPitch>
        <Box>
          <ItemGrid>
            {visibleItems(category.items, isLoggedIn).map((item) => (
              <NavItem
                key={item.title}
                item={item}
                pathname={pathname}
                onNavigate={onNavigate}
              />
            ))}
          </ItemGrid>
        </Box>
      </PanelInner>
    </Panel>
  );
};
